import AlertInline from '@storeblocks/inline-alert';
import { LinearLoadingIndicator } from '@storeblocks/linear-loading-indicator';
import { type FC } from 'react';
import styled from 'styled-components';

import { LinkButton } from '@/components/shared/LinkButton';
import { useHandleAsyncOperation } from '@/hooks/useHandleAsyncError';
import { useTexts } from '@/hooks/useTexts';
import { TrackingElement, useGetTrackId } from '@/tracking';
import { stripFilename } from '@/util/stripFilename';

import { FileTypeIcon } from '../FileTypeIcon/FileTypeIcon';

interface Props {
  filename: string;
  fileId: string;
  fileExtension: string;
  onDownload: (fileId: string) => Promise<unknown>;
}

export const FileNameColumn: FC<Props> = ({
  filename,
  fileId,
  fileExtension,
  onDownload,
}: Props) => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const startDownload = async (): Promise<void> => {
    await onDownload(fileId);
  };

  const [handleDownloadClick, isDownloading, isError] =
    useHandleAsyncOperation(startDownload);

  return (
    <Wrapper>
      <FileIconWrapper key="icon">
        <FileTypeIcon fileExtension={fileExtension} />
      </FileIconWrapper>

      <NameWrapper>
        {!isDownloading && (
          <StyledLinkButton
            key="anchor"
            onClick={handleDownloadClick}
            data-trackid={getTrackId(
              'download-clicked-file',
              TrackingElement.Link,
            )}
            data-trackid-downloaded-file={stripFilename(filename)}
          >
            {filename}
          </StyledLinkButton>
        )}

        {isError && (
          <AlertInline variant="error" title={texts.download.singleFileError} />
        )}
      </NameWrapper>

      {isDownloading && (
        <LinearLoadingWrapper>
          <LinearLoadingIndicator type="indeterminate" />
        </LinearLoadingWrapper>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
`;

const StyledLinkButton = styled(LinkButton)`
  margin-left: 0;
  word-break: break-all;
`;

const NameWrapper = styled.div`
  padding: 16px 0;
  line-height: 24px;
  white-space: normal;
`;

const LinearLoadingWrapper = styled.div`
  justify-content: flex-start;
  align-self: center;
  height: 24px;
  padding: 16px 0;
`;

const FileIconWrapper = styled.span`
  padding: 16px 16px 0 16px;
`;
